import Footer from "./Footer";
import Top from "./Top";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [paying, setPaying] = useState(false);

  const userId = JSON.parse(
    localStorage.getItem("SLNUserDetails") || "{}",
  )?.userId;
  const token = localStorage.getItem("SLNToken");
  const user = JSON.parse(localStorage.getItem("SLNUserDetails") || "{}");

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };

  const fetchCart = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/CartAndWishlist/GetCoursesFromCartOrWishlist?userId=${userId}&type=cart`,
        { headers },
      );
      const data = await res.json();
      const raw = data?.data;
      const items = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.cart)
          ? raw.cart
          : Array.isArray(raw?.items)
            ? raw.items
            : [];
      setCartItems(items);
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (courseId) => {
    try {
      setRemovingId(courseId);
      const res = await fetch(
        "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/CartAndWishlist/DeleteFromCartOrWishlist",
        {
          method: "DELETE",
          headers,
          body: JSON.stringify({ type: "cart", userId, courseId }),
        },
      );
      if (!res.ok) throw new Error("Failed to remove");
      setCartItems((prev) => prev.filter((item) => item.courseId !== courseId));
    } catch (err) {
      console.error("Remove failed:", err);
      alert("Failed to remove item.");
    } finally {
      setRemovingId(null);
    }
  };

  useEffect(() => {
    if (userId) fetchCart();
    else setLoading(false);
  }, []);

  // Load Razorpay script dynamically
  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  // Step 1 — Create order on backend (per course)
  const createOrder = async (courseId) => {
    const res = await fetch(
      "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Payment/CreateOrder",
      {
        method: "POST",
        headers,
        body: JSON.stringify({ userId, courseId, includeGST: true }),
      },
    );
    if (!res.ok) throw new Error("Failed to create order");
    const data = await res.json();
    return data?.data || data;
  };

  // Step 2 — Confirm payment on backend after Razorpay success
  const confirmPayment = async (
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
  ) => {
    const res = await fetch(
      "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Payment/ConfirmPayment",
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          razorpay_order_id,
          razorpay_payment_id,
          razorpay_signature,
        }),
      },
    );
    if (!res.ok) throw new Error("Payment confirmation failed");
    return res.json();
  };

  const handlePayment = async () => {
    if (cartItems.length === 0) return;

    try {
      setPaying(true);

      const loaded = await loadRazorpay();
      if (!loaded) {
        alert(
          "Failed to load Razorpay. Please check your internet connection.",
        );
        setPaying(false);
        return;
      }

      // Create order for first course — Razorpay session uses this order
      // For multi-course carts, one Razorpay session covers the total,
      // then ConfirmPayment is called per course with the same payment IDs
      const order = await createOrder(cartItems[0].courseId);

      const options = {
        key: order.key, // returned by backend
        amount: order.amount, // in paise, returned by backend
        currency: order.currency || "INR",
        order_id: order.orderId, // Razorpay order ID from backend
        name: "SLN Academy",
        description: `${cartItems.length} Course${cartItems.length > 1 ? "s" : ""}`,
        image: "/assets/images/logo.png",
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.mobile || user?.phone || "",
        },
        notes: {
          userId,
          courseIds: cartItems.map((i) => i.courseId).join(","),
        },
        theme: {
          color: "#1D9E75",
        },
        handler: async (response) => {
          try {
            // Confirm payment for every course in the cart
            await Promise.all(
              cartItems.map((item) =>
                confirmPayment(
                  response.razorpay_order_id,
                  response.razorpay_payment_id,
                  response.razorpay_signature,
                ),
              ),
            );
            alert(
              `Payment successful! Payment ID: ${response.razorpay_payment_id}`,
            );
            setCartItems([]);
          } catch (err) {
            console.error("Confirm payment error:", err);
            alert(
              "Payment received but confirmation failed. Please contact support with Payment ID: " +
                response.razorpay_payment_id,
            );
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", (response) => {
        console.error("Payment failed:", response.error);
        alert(`Payment failed: ${response.error.description}`);
        setPaying(false);
      });

      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      alert(err.message || "Something went wrong. Please try again.");
      setPaying(false);
    }
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + Number(item.courseDetails?.offeredPrice || 0),
    0,
  );
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  return (
    <>
      <Top />

      <main className="pb-5 pt-4">
        <section className="container">
          <div className="row g-3">
            {/* Cart Items */}
            <div className="col-lg-8 col-12">
              <div className="mb-4">
                <span className="text-uppercase text-secondary fw-semibold ls-md">
                  MY CART
                </span>
                <h2 className="fw-semibold ls-sm text-success lh-1 mt-0">
                  Checkout &mdash; My Courses
                </h2>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                    alt=""
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-success" />
                </div>
              ) : cartItems.length === 0 ? (
                <div className="card shadow-none rounded-4 bg-danger-soft">
                  <div className="card-body d-flex align-items-center">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/9374/9374373.png"
                      width="60"
                      alt=""
                    />
                    <div className="ms-3">
                      <h4 className="mb-0 lh-1">Your cart is empty</h4>
                      <p className="text-muted mb-0">
                        Add some courses to see them here.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {cartItems.map((item) => {
                    const course = item.courseDetails;
                    const discount =
                      course?.originalPrice && course?.offeredPrice
                        ? Math.round(
                            ((course.originalPrice - course.offeredPrice) /
                              course.originalPrice) *
                              100,
                          )
                        : null;

                    return (
                      <div
                        key={item.courseId}
                        className="card rounded-4 shadow-none border"
                      >
                        <div className="card-body d-flex align-items-start gap-3">
                          <img
                            src={course?.banner || "/assets/images/1.jpg"}
                            alt={course?.title}
                            className="rounded-4 object-fit-cover flex-shrink-0"
                            width="150"
                          />
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <h5 className="mb-0 lh-sm fs-5 text-dark">
                                  <Link
                                    to={`/course/${item.courseId}`}
                                    className="text-dark"
                                  >
                                    {course?.title}
                                  </Link>
                                </h5>
                                {course?.instructors?.[0] && (
                                  <div className="text-muted small mb-2">
                                    By {course.instructors[0].name}
                                    {course.instructors[0].rating && (
                                      <span className="ms-2 text-warning">
                                        ★ {course.instructors[0].rating}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                className="btn btn-outline-danger btn-sm rounded-3 flex-shrink-0"
                                onClick={() => handleRemove(item.courseId)}
                                disabled={removingId === item.courseId}
                              >
                                {removingId === item.courseId ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="bi bi-trash3" />
                                )}
                              </button>
                            </div>

                            {/* Badges */}
                            <div className="d-flex flex-wrap gap-1 mb-1">
                              {course?.category && (
                                <span className="badge bg-success-subtle px-3 text-success rounded-pill">
                                  {course.category}
                                </span>
                              )}
                            </div>
                            <div className="d-flex flex-wrap gap-1 mb-1">
                              {course?.lessons?.length > 0 && (
                                <span className="badge bg-dark-subtle px-3 rounded-pill">
                                  {course.lessons.length} lessons
                                </span>
                              )}
                            </div>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              {course?.certification && (
                                <span className="badge bg-dark-subtle px-3 text-muted rounded-pill">
                                  <i className="bi bi-patch-check me-1" />
                                  Certificate
                                </span>
                              )}
                            </div>

                            {/* Price row */}
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className="fw-semibold text-dark">
                                ₹
                                {Number(
                                  course?.offeredPrice || 0,
                                ).toLocaleString("en-IN")}
                              </span>
                              {course?.originalPrice && (
                                <span className="text-muted text-decoration-line-through small">
                                  ₹
                                  {Number(course.originalPrice).toLocaleString(
                                    "en-IN",
                                  )}
                                </span>
                              )}
                              {discount && (
                                <span className="badge bg-success-subtle text-success rounded-pill">
                                  {discount}% off
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Price Summary */}
            <div className="col-lg-4 col-12">
              <div
                className="card shadow-none rounded-4 p-2 bg-light sticky-top"
                style={{ top: 80 }}
              >
                <div className="card-body">
                  <h4 className="mb-3 fw-semibold">Price Details</h4>

                  <div className="d-flex justify-content-between">
                    <span>
                      Subtotal ({cartItems.length} item
                      {cartItems.length !== 1 ? "s" : ""})
                    </span>
                    <span className="text-dark fw-medium">
                      ₹{subtotal.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mt-1">
                    <span>GST [18%]</span>
                    <span className="text-dark fw-medium">
                      ₹{gst.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <hr />

                  <div className="d-flex justify-content-between mb-3">
                    <span className="fw-semibold text-dark">Total Amount</span>
                    <strong className="text-primary fw-semibold">
                      ₹{total.toLocaleString("en-IN")}
                    </strong>
                  </div>

                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold rounded-pill mt-1"
                    type="button"
                    disabled={cartItems.length === 0 || paying}
                    onClick={handlePayment}
                  >
                    {paying ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Processing ...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-lock-fill me-2" />
                        Pay ₹{total.toLocaleString("en-IN")}
                      </>
                    )}
                  </button>

                  <div className="text-muted small text-center mt-2 d-flex align-items-center justify-content-center gap-1">
                    <i className="bi bi-shield-check text-success" />
                    Secured by Razorpay
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Cart;
